import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';
import type { Config, Data, Layout } from 'plotly.js';

import type { ChartConfig } from '../types/dashboard';
import { useDashboardData } from '../hooks/useDashboardData';
import { useChartExport } from '../context/chartExportContext';
// Side-effect import: loading the barrel registers every built-in chart type.
import { getChart, normalizeConfig, DEFAULT_PALETTE } from '../charts';
import type { RenderContext } from '../charts';
import {
  makeLayout,
  resolveLimitsShapes,
  resolveTitleBlock,
  resolveLegendGutter,
  legendTakesGutter,
} from '../charts/shared/layout';
import { countLegendEntries, estimateLegendHeight } from '../charts/shared/legend';
import { chartDataToCsv, downloadCsv } from '../charts/shared/csv';
import { fitTextToBox, measureTextWidth, wrapLines } from '../charts/shared/text';
import { figureScale, scaleFont, scalePx } from '../charts/theme/scale';
import {
  CHART_FONT,
  GRID_COLOR,
  STATS_BOX_PAD,
  STATS_BOX_INSIDE_FRACTION,
  STATS_FONT_MIN_SIZE,
  STATS_BOX_MAX_HEIGHT_FRACTION,
  STATS_LINE_HEIGHT,
  STATS_BOX_CHROME,
  STATS_BOX_BORDERPAD,
  STATS_BOX_BORDER_WIDTH,
  STATS_GUTTER,
  STATS_VALUE_RIGHT,
  STATS_GROUP_GAP_LINES,
  STATS_COLUMN_GAP,
} from '../charts/theme/layout';

// Plotly is heavy (~1MB+ gzipped). Load it on demand so consumers that never
// render a chart don't pay for it in their initial bundle.
const Plot = lazy(() => import('react-plotly.js'));

// Axis keys plotly_relayout can report a range/autorange edit under, e.g.
// 'xaxis', 'yaxis2'. See the liveAxisRanges state below (SPEC/2235).
const AXIS_RANGE_KEY = /^([a-z]+axis\d*)\.range(?:\[(0|1)\])?$/;
const AXIS_AUTORANGE_KEY = /^([a-z]+axis\d*)\.autorange$/;

// Safety net for `pendingDoubleClickReset` below: the flag is meant to be
// consumed by the very next `plotly_relayout`, which dragbox.js's own
// `doubleClick()` always fires. But that is an internal plotly.js ordering
// guarantee, not a public contract — if a double-click ever lands with no
// following relayout (an unsupported chart type, a future plotly.js
// version), an un-timed flag would stay stuck `true` and silently misfile
// the user's NEXT real zoom as a reset. A generous window (well over what a
// same-tick/microtask-away relayout needs) clears it defensively instead.
const DOUBLE_CLICK_RESET_WINDOW_MS = 500;

// The "home" icon plotly.js's own resetScale2d button draws (fonts/ploticon.js),
// copied as a literal so the custom reset button below (SPEC/2235 follow-up 2)
// looks identical to the built-in one it replaces, without a static `import
// 'plotly.js'` that would pull the whole library out of the lazy `Plot` chunk.
const RESET_AXES_ICON = {
  width: 928.6,
  height: 1000,
  path: 'm786 296v-267q0-15-11-26t-25-10h-214v214h-143v-214h-214q-15 0-25 10t-11 26v267q0 1 0 2t0 2l321 264 321-264q1-1 1-4z m124 39l-34-41q-5-5-12-6h-2q-7 0-12 3l-386 322-386-322q-7-4-13-4-7 2-12 7l-35 41q-4 5-3 13t6 12l401 334q18 15 42 15t43-15l136-114v109q0 8 5 13t13 5h107q8 0 13-5t5-13v-227l122-102q5-5 6-12t-4-13z',
  transform: 'matrix(1 0 0 -1 0 850)',
};

// An arrow into a tray, for the "Download data" button (US 2488). Plain SVG
// coordinates: without `transform` or `ascent`, plotly.js draws the path as-is.
const DOWNLOAD_DATA_ICON = {
  width: 24,
  height: 24,
  path: 'M10 2h4v9h4l-6 7-6-7h4z M2 15h3v4h14v-4h3v7H2z',
};

/**
 * Pulls the hover modebar flush to the figure's top edge.
 *
 * Plotly's own stylesheet sets `.modebar { top: 2px }` — no inline style, and
 * `layout.modebar` exposes no position — so those 2px are only reachable through
 * CSS. The title no longer buys its way clear of the bar's 25px band (see
 * TITLE_PAD_TOP: 24px of permanent vertical space in every figure, to avoid an
 * overlap that only happens on hover, was a bad trade). What is left is worth
 * keeping anyway: two fewer pixels of a translucent bar over a long centered
 * title, at no cost.
 *
 * Injected from here rather than from the kit's index.css because that stylesheet
 * is not part of the package's public surface — consumers import components, not
 * CSS, so a rule left there would never reach them.
 */
const MODEBAR_TOP_RULE = '.js-plotly-plot .modebar { top: 0 !important; }';
let modebarRuleInjected = false;
function ensureModebarFlushTop(): void {
  if (modebarRuleInjected || typeof document === 'undefined') return;
  modebarRuleInjected = true;
  const style = document.createElement('style');
  style.dataset.dashboardKit = 'modebar-top';
  style.textContent = MODEBAR_TOP_RULE;
  document.head.appendChild(style);
}

interface ChartRendererProps {
  config: ChartConfig;
}

/** Slugify a chart title into a safe filename for the PNG and CSV export buttons. */
function exportFilename(title: string): string {
  const slug = (title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'chart';
}

/** One statistic as the consumer supplies it. `group` is optional; see ChartConfig. */
type StatEntry = { label: string; value: string | number; group?: string };

/** One drawn line of the gutter table: a heading, a spacer, or a label/value row. */
type StatLine = { label: string; value: string };

/**
 * Placeholder for a line with nothing in it (a spacer, or the value side of a
 * heading row).
 *
 * A non-breaking space and NOT an empty string: Plotly drops empty leading lines
 * when it lays a text run into tspans, so the value column — which starts blank,
 * opposite the first heading — came out one row SHORT and every number sat against
 * the label above its own (measured: "12191" printed next to `ANÁLISIS` instead of
 * next to `N`). The two columns must render the same number of lines, not merely
 * contain the same number of separators.
 */
const BLANK_LINE = '\u00a0';

/**
 * Leading indent for a value that had to drop below its label. Non-breaking
 * spaces, so Plotly keeps them: it collapses ordinary leading whitespace in a
 * text run, which would put the continuation flush with its own label and destroy
 * the very hierarchy the indent exists to show.
 */
const STATS_INDENT = '\u00a0\u00a0\u00a0';

/**
 * Turn the flat entry list into the line stack the gutter draws: ungrouped
 * entries first, then one heading per group (blank-line separated) followed by
 * its rows. Group order is first appearance, so the consumer controls it by the
 * order it emits entries in — the kit sorts nothing.
 */
function statsLines(stats: StatEntry[]): StatLine[] {
  const row = (s: StatEntry): StatLine => ({ label: s.label, value: String(s.value) });
  const lines: StatLine[] = stats.filter((s) => !s.group).map(row);
  const groups: string[] = [];
  for (const s of stats) if (s.group && !groups.includes(s.group)) groups.push(s.group);
  for (const g of groups) {
    // No leading blank on the very first block, so the stack starts flush at the top.
    if (lines.length)
      for (let i = 0; i < STATS_GROUP_GAP_LINES; i++)
        lines.push({ label: BLANK_LINE, value: BLANK_LINE });
    lines.push({ label: `<b>${g}</b>`, value: BLANK_LINE });
    lines.push(...stats.filter((s) => s.group === g).map(row));
  }
  return lines;
}

/**
 * Stats annotation(s).
 *
 * Default: a two-column table — labels flush left, values flush right — either in
 * the reserved right-hand gutter (`right`) or top-left inside the plot. Entries
 * carrying a `group` are drawn under a heading, in first-appearance order.
 *
 * With `columns` (only meaningful with `right`), each entry becomes its OWN
 * side-by-side column instead, for a few PARALLEL blocks (e.g. LP vs CP on the
 * histogram, which read as two things being compared, not as one list). That
 * variant keeps the wrapped-text treatment: its entries are whole blocks whose
 * `value` may carry `<br>` lines, not label/value rows. `columns` is NOT for many
 * single-line entries (the scatter's N/correlation/regression set), which would
 * overflow the gutter.
 */
function statsAnnotations(
  stats: StatEntry[],
  right = false,
  columns = false,
  paper?: { width: number; height: number },
  // Proportional sizing, same as every other in-figure element (see charts/theme/
  // scale): held absolute, this box read as a different size class in a card and
  // in a report figure.
  scale = 1
): Record<string, unknown>[] {
  const font = scaleFont(CHART_FONT, scale);
  // Nothing bounded these boxes before, so they grew to whatever the text needed
  // and were clipped by the figure (measured on the scatter: a 318px box starting
  // at x=327 in a 508px figure — 137px lost, cutting `σ` and the correlation
  // mid-value) or covered each other (the histogram's LP column overlapped 17px
  // under CP's opaque background, so LP read "mediana=0." instead of
  // "mediana=0.295"). Wrap every entry to the px actually available in its slot.
  // Budgets come from the PAPER box (the plot area between the margins), not the
  // figure: these annotations are positioned with xref/yref 'paper', so a fraction
  // of the figure overstates the room by the margins — up to 62% with a wide right
  // gutter — and the box was still wrapped too wide to fit.
  const slotPx = (fraction: number) =>
    paper && paper.width > 0 ? paper.width * fraction - scalePx(STATS_BOX_PAD, scale) : 0;
  // One call decides both levers, because they interact: wrapping to the slot can
  // make the box too tall, and stepping the font down re-wraps it into fewer lines.
  // Never grows the font, and never goes below STATS_FONT_MIN_SIZE — itself scaled,
  // since a 9px floor tuned for a card would be illegible once a 1000px report
  // figure is shrunk into a PDF column.
  const fit = (text: string, fraction: number) =>
    fitTextToBox(
      text,
      slotPx(fraction),
      paper ? paper.height * STATS_BOX_MAX_HEIGHT_FRACTION : 0,
      font,
      scalePx(STATS_FONT_MIN_SIZE, scale),
      STATS_LINE_HEIGHT,
      scalePx(STATS_BOX_CHROME, scale)
    );
  const base = {
    xref: 'paper',
    yref: 'paper',
    xanchor: 'left',
    yanchor: 'top',
    align: 'left',
    showarrow: false,
    font,
    bgcolor: 'rgba(255,255,255,0.85)',
    bordercolor: GRID_COLOR,
    // Borderless, like the legend: the outline boxed the statistics off as a
    // separate panel. The translucent background is what keeps them readable over
    // the grid, so it stays.
    borderwidth: STATS_BOX_BORDER_WIDTH,
    borderpad: scalePx(STATS_BOX_BORDERPAD, scale),
  };
  if (right && columns) {
    // Right gutter [0.62, 0.99]: one column per entry, evenly spaced; the label
    // is a bold header above its value lines. Pinned below the top legend. Each
    // column is wrapped to its OWN step, so a wide entry can no longer reach
    // under its neighbour's background.
    const leftEdge = STATS_GUTTER.columnsStart;
    const step = (STATS_GUTTER.end - leftEdge) / Math.max(stats.length, 1);
    return stats.map((s, i) => {
      const fitted = fit(`<b>${s.label}:</b><br>${s.value}`, step);
      return {
        ...base,
        x: leftEdge + i * step,
        y: 0.82,
        font: { ...font, size: fitted.size },
        text: fitted.text,
      };
    });
  }
  // Single stacked block: top-left inside the plot, or in the right gutter. It
  // starts at the TOP (0.98, not the old 0.55): 0.55 existed to clear an inside
  // top-right legend, and the legend now lives below the plot (see LEGEND_STYLE).
  //
  // TWO annotations, not one: labels flush left and values flush right, sharing
  // one line stack so the rows line up as a table. Plotly cannot align columns
  // inside a single text run — packing "label: value" into one left-aligned run
  // is what left the numbers ragged and let a long entry wrap into an orphan line
  // under its neighbour. Both must carry the SAME font size or the two columns
  // drift apart line by line, hence one fit for the pair.
  const fraction = right
    ? STATS_GUTTER.end - STATS_GUTTER.singleStart
    : STATS_BOX_INSIDE_FRACTION;
  const slot = slotPx(fraction);
  const gap = scalePx(STATS_COLUMN_GAP, scale);
  const minSize = scalePx(STATS_FONT_MIN_SIZE, scale);
  const maxHeight = paper ? paper.height * STATS_BOX_MAX_HEIGHT_FRACTION : 0;

  /**
   * The rows as they would be drawn at `size`.
   *
   * A value that still does not fit beside its label — a legacy entry packing
   * `μ=… σ=… min=… max=…` into one string, or any `<br>` block — drops BELOW its
   * label instead, wrapped to the full column. Right-aligned, an overlong value
   * grows leftwards and lands on top of the labels, which is worse than the orphan
   * line the table set out to avoid: the numbers become unreadable rather than
   * merely ugly. Fresh data no longer produces such values (conciliatron emits one
   * row per statistic), but saved dashboards carry the old shape.
   */
  const rowsAt = (size: number): { lines: StatLine[]; overflowed: boolean } => {
    const at = { ...font, size };
    const out: StatLine[] = [];
    let overflowed = false;
    for (const line of statsLines(stats)) {
      const isBlank = line.value === BLANK_LINE;
      const fitsBeside =
        slot <= 0 ||
        measureTextWidth(line.label, at) + gap + measureTextWidth(line.value, at) <= slot;
      if (isBlank || fitsBeside) {
        out.push(line);
        continue;
      }
      overflowed = true;
      // The value goes UNDER its label, indented and left-aligned in the label
      // column — not right-aligned in the value column. Right-alignment is for
      // atomic numbers, where a column of decimal points is what makes them
      // comparable; a run of prose set flush right reads as broken typesetting,
      // which is not something to put in a technical report. Indented under its
      // label it reads as a continuation of it, and it gets the FULL column width
      // rather than what is left beside the label, so it wraps into fewer lines.
      out.push({ label: line.label, value: BLANK_LINE });
      const body = wrapLines(line.value, slot - measureTextWidth(STATS_INDENT, at), at);
      for (const w of body.split('<br>'))
        out.push({ label: `${STATS_INDENT}${w}`, value: BLANK_LINE });
    }
    return { lines: out, overflowed };
  };

  // Shrink FIRST and only drop values below their labels at the floor: a smaller
  // font keeps the table a table, which reads better than a stack of blocks.
  let size: number = font.size;
  for (;;) {
    const { lines: candidate, overflowed } = rowsAt(size);
    const tooTall =
      maxHeight > 0 &&
      candidate.length * size * STATS_LINE_HEIGHT + scalePx(STATS_BOX_CHROME, scale) > maxHeight;
    if ((!overflowed && !tooTall) || size <= minSize) break;
    size = Math.max(minSize, size - 1);
  }
  const lines = rowsAt(size).lines;
  const column = (
    x: number,
    xanchor: 'left' | 'right',
    pick: (l: StatLine) => string
  ) => ({
    ...base,
    // The gutter table draws no surface at all: it is a column of the card, not a
    // panel floating over it. The inside variant keeps its background, which is
    // what makes it readable where it sits on top of the data.
    ...(right ? { bgcolor: 'rgba(0,0,0,0)' } : {}),
    x,
    xanchor,
    align: xanchor,
    y: 0.98,
    font: { ...font, size },
    text: lines.map(pick).join('<br>'),
  });
  const leftEdge = right ? STATS_GUTTER.singleStart : 0.02;
  return [
    column(leftEdge, 'left', (l) => l.label),
    column(right ? STATS_VALUE_RIGHT : leftEdge + STATS_BOX_INSIDE_FRACTION, 'right', (l) => l.value),
  ];
}

/**
 * Renders a chart by delegating to the registered chart type. The renderer no
 * longer knows about any specific chart kind: it normalizes the config, asks
 * the registry for the type's `buildTraces`/`buildLayout`, and draws. Adding a
 * chart type means adding a module under `charts/<type>/`, never editing here.
 */
/**
 * How many times one figure geometry may re-read the plot's left edge before the
 * value is taken as settled. Two: the authored margin, then the measured one — the
 * documented intent. A third is allowed as slack for a genuine second-order shift
 * (a level change widening the tick labels without resizing the card); past that a
 * change is the feedback loop described at `measurePlotLeft`, not new information.
 */
const PLOT_LEFT_MAX_PASSES = 3;

/**
 * The figure size in whole CSS px, rounded the same way for both readers: the
 * synchronous first read (`getComputedStyle`) and the ResizeObserver
 * (`contentRect`). Both report the unsnapped layout size of this padding-less
 * wrapper, untouched by CSS transforms. `clientWidth/Height` would not do: it is
 * pixel-snapped, so on a fractional width it can land 1px off the observer's
 * rounding and its first report would redraw a figure that just drew (SPEC/2539).
 */
const toFigureSize = (width: number, height: number) => ({
  w: Math.round(Number.isFinite(width) ? width : 0),
  h: Math.round(Number.isFinite(height) ? height : 0),
});

/**
 * The synchronous first read. An element with no box (itself or an ancestor
 * `display: none`) has no used size: `getComputedStyle` then returns the COMPUTED
 * value, `100%` for this wrapper, which `parseFloat` would read as 100px. So no
 * box, or anything that is not a px length, is 0x0 — "not measured", same as the
 * observer reports for it.
 */
function readFigureSize(el: HTMLElement) {
  if (el.getClientRects().length === 0) return toFigureSize(0, 0);
  const style = getComputedStyle(el);
  const px = (v: string) => (v.endsWith('px') ? parseFloat(v) : 0);
  return toFigureSize(px(style.width), px(style.height));
}

export function ChartRenderer({ config }: ChartRendererProps) {
  const { type, options } = config;
  const { getDataset } = useDashboardData();
  // Measured figure width, fed to the layout so in-figure text can be wrapped to
  // the pixels actually available (see charts/shared/text). Read once before the
  // first paint and then kept current by a ResizeObserver (see the layout effect
  // below); it stays 0 where there is no observer, which makes every fit a no-op.
  const sizeRef = useRef<HTMLDivElement | null>(null);
  const [figureSize, setFigureSize] = useState({ w: 0, h: 0 });
  // Whether that first read has happened — any size, 0x0 included. Plotly is not
  // handed a figure before it (see the `pending` gate below).
  const [measuredOnce, setMeasuredOnce] = useState(false);
  // Canvas `measureText` resolves the family against the fonts ALREADY loaded in
  // the document, and Inter arrives as a remote webfont with `display=swap` (see
  // ui-kit's fonts.css). A first measurement can land before Inter is available and
  // silently use fallback metrics; a font swap resizes nothing, so nothing would
  // recompute and the wrong wrap would be baked into the exported PNG.
  //
  // This RE-FITS when the fonts land rather than waiting for them: gating the fit
  // on `document.fonts.ready` disabled it outright wherever that promise never
  // settles (jsdom, and any environment without the API), which is a worse failure
  // than one early fit against fallback metrics. The bump is a dependency of the
  // memoized `figure` below, so the layout — and with it the wrap — recomputes.
  const [fontEpoch, bumpFontEpoch] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;
    let cancelled = false;
    void document.fonts.ready.then(() => {
      if (!cancelled) bumpFontEpoch((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // A LAYOUT effect, so the first read re-renders before the browser paints: the
  // chart's first painted frame is already the measured figure. The read is
  // synchronous rather than left to the observer because an observer only reports
  // on `observe()` for an element that is rendered with a non-zero size — a hidden
  // container would otherwise never draw. Both readers go through `toFigureSize`,
  // so the observer's first report matches this read and redraws nothing.
  useLayoutEffect(() => {
    ensureModebarFlushTop();
    const el = sizeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const first = readFigureSize(el);
    setFigureSize((prev) => (prev.w === first.w && prev.h === first.h ? prev : first));
    setMeasuredOnce(true);
    const ro = new ResizeObserver(([entry]) => {
      // Round to whole px: sub-pixel jitter during a resize would otherwise
      // re-wrap (and redraw) on every animation frame.
      const { w, h } = toFigureSize(entry.contentRect.width, entry.contentRect.height);
      setFigureSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Where the PLOT's left edge actually lands inside the figure, read back from
  // the drawn graph div. `automargin` resolves the left margin from the Y-axis
  // title and tick labels, which nothing here can predict — on the accuracy map it
  // reaches ~126px against an authored 60 — and the header is anchored to that
  // edge, so its wrap budget depends on it. Read from the plot background's box
  // (public DOM), not Plotly's private `_fullLayout`.
  //
  // A second pass: the first draw uses the authored margin (the container itself is
  // already measured before it, see the layout effect above), this reports the real
  // one, and the figure redraws once more with the right budget.
  //
  // That was argued to converge because "wrapping the header cannot move the left
  // margin — that margin is set by the axis, which the header does not touch". True
  // of the MARGIN, and this reads the DOMAIN: `.nsewdrag` covers the axis' domain
  // box, and on an `equalAspect` chart `constrain: 'domain'` shrinks that box to keep
  // metres square, `constraintoward` centring what it shrinks. So the header CAN move
  // it, all the way round: a taller wrapped subtitle grows the top margin, a shorter
  // plot needs a narrower domain, centring splits the loss between the two sides, and
  // the drag layer's left edge steps right — cutting the budget that made the subtitle
  // wrap in the first place. Each pass wraps one line more. It does not oscillate, it
  // runs away, and every step is a Plotly redraw: the map redrawing without end.
  //
  // Bounded rather than unwound, because the dependency is real: the header is
  // anchored to that edge and has to know where it is. What is NOT real is needing to
  // know it more than a couple of times per geometry — the documented intent was one
  // extra pass. The count resets whenever the figure is actually resized, which is the
  // only event that legitimately invalidates the measurement.
  const [plotLeft, setPlotLeft] = useState<number | undefined>(undefined);
  const [plotWidth, setPlotWidth] = useState<number | undefined>(undefined);
  const [plotArea, setPlotArea] = useState<{ width: number; height: number } | undefined>(undefined);
  const measuresLeft = useRef({ key: '', n: 0 });
  const measurePlotLeft = (gd: HTMLElement) => {
    const key = `${figureSize.w}x${figureSize.h}`;
    if (measuresLeft.current.key !== key) measuresLeft.current = { key, n: 0 };
    if (measuresLeft.current.n >= PLOT_LEFT_MAX_PASSES) return;
    measuresLeft.current.n += 1;
    measureOnce(gd);
  };
  const measureOnce = (gd: HTMLElement) => {
    const svg = gd.querySelector('.main-svg');
    // `.nsewdrag` is the drag layer, which covers EXACTLY the cartesian plot area.
    // Not `.bg`: the only `.bg` rect a chart is guaranteed to have is the LEGEND's
    // (measured on the accuracy map, whose plot area has none), and reading that
    // one reported a 634px left margin on an 885px figure — so the header wrapped
    // to a sliver instead of to the real width. Absent on non-cartesian types
    // (pie), where falling back to the authored margin is right anyway: they grow
    // no axis labels.
    const plot = gd.querySelector('.nsewdrag');
    if (!svg || !plot) return;
    const left = Math.round(plot.getBoundingClientRect().left - svg.getBoundingClientRect().left);
    // Sub-pixel jitter would otherwise redraw on every update; and a 0 means the
    // figure is not laid out yet (hidden tab, zero-size container).
    if (left > 0) setPlotLeft((prev) => (prev !== undefined && Math.abs(prev - left) <= 1 ? prev : left));
    // The same measurement gives the plot's real WIDTH, which is what the stats box
    // needs: its slot is a fraction of the between-margins box, and neither margin is
    // knowable up front (the left grows with the tick labels, the right with the
    // legend's reserved gutter). Same jitter guard, same convergence argument.
    const w = Math.round(plot.getBoundingClientRect().width);
    if (w > 0) setPlotWidth((prev) => (prev !== undefined && Math.abs(prev - w) <= 1 ? prev : w));
  };
  // The plot box in LAYOUT px, which a plan view's `fitMarkerToCell` turns the grid
  // pitch into marker px with (Bug 2720). Read on EVERY draw, not within the bounded
  // passes above: a bench switch re-wraps the subtitle (it names the bench), which
  // moves the domain long after those passes are spent, and a stale box sized the
  // blocks for a larger plot than the one drawn. No runaway to bound here — a marker
  // size moves neither a margin nor a domain, so the next draw measures the same box.
  // Only for charts that use it, so no other chart redraws on a box change. A bounding
  // rect includes ancestor transforms — the report preview is drawn through
  // `scale(0.45)` — so it is divided by the graph div's rendered-to-layout ratio.
  const measurePlotArea = (gd: HTMLElement) => {
    if (!options?.fitMarkerToCell) return;
    const plot = gd.querySelector('.nsewdrag');
    if (!plot) return;
    const ratio = gd.offsetWidth > 0 ? gd.getBoundingClientRect().width / gd.offsetWidth : 1;
    if (!(ratio > 0)) return;
    const rect = plot.getBoundingClientRect();
    const area = { width: Math.round(rect.width / ratio), height: Math.round(rect.height / ratio) };
    if (area.width > 0 && area.height > 0)
      setPlotArea((prev) =>
        prev && Math.abs(prev.width - area.width) <= 1 && Math.abs(prev.height - area.height) <= 1 ? prev : area
      );
  };

  // Register this chart's Plotly graph div for PNG export (SPEC/1466 phase 3).
  // No-op when no ChartExportProvider is mounted. Drop it on unmount so a
  // removed chart never gets exported into a report.
  const { register, unregister } = useChartExport();
  useEffect(() => () => unregister(config.id), [config.id, unregister]);

  // Drives `layout.uirevision` (SPEC/2235): Plotly only keeps a user's interactive
  // zoom/pan across a `Plotly.react()` call when `uirevision` is IDENTICAL to the
  // previous one. Left unset, every re-render re-applies the authored axis range
  // verbatim and silently discards whatever the user zoomed to — which is what
  // happened on every plan-view level/elevation flip, since ChartCard's
  // `renderConfig` only rewrites `filters` and keeps the same `config.data` array.
  // Bumped only when that array's reference changes, which a genuine re-run does
  // (a fresh dataset) but a level flip does not — the pattern React itself
  // recommends for deriving state from a prop across renders, not an effect, so
  // the new revision is ready in time for this render's `layout`.
  const [dataIdentity, setDataIdentity] = useState(config.data);
  const [dataRevision, setDataRevision] = useState(0);
  const dataChanged = config.data !== dataIdentity;
  if (dataChanged) {
    setDataIdentity(config.data);
    setDataRevision((r) => r + 1);
  }

  // The user's live zoom/pan, captured straight from Plotly's own relayout
  // events instead of relying on `layout.uirevision` to restore it (SPEC/2235
  // follow-up). `uirevision` alone isn't enough for an `equalAspect` map
  // (`scaleanchor` + `constrain: 'domain'`, see charts/shared/layout.ts): when
  // an elevation flip's authored range differs from the range Plotly captured
  // at first mount on even ONE endpoint (`xaxis.range[1]` say), Plotly restores
  // that endpoint from the stale pre-zoom value instead of the just-offered
  // one, and the resulting inconsistent range trips the aspect-ratio solver
  // into a full autorange — silently discarding the rest of the zoom too.
  // Keyed by axis (`xaxis`, `yaxis2`, …) so a dual-axis chart tracks each
  // independently. Cleared on a genuine re-run (`dataChanged`), same trigger
  // as `dataRevision`, so a fresh dataset draws at the authored frame.
  const [liveAxisRanges, setLiveAxisRanges] = useState<Record<string, [number, number]>>({});
  if (dataChanged && Object.keys(liveAxisRanges).length > 0) {
    setLiveAxisRanges({});
  }
  // A double-click reset is NOT a modebar button click (see `resetAxesButton`
  // below) — it's plotly.js's own dragbox.js handler, which performs its own
  // internal relayout and then fires `plotly_doubleclick` with no payload, so
  // there's nothing in that event to act on directly. What we CAN use is
  // ORDER: that internal relayout fires a `plotly_relayout` (our `onRelayout`
  // below) carrying exactly the axes it touched, and `plotly_doubleclick`
  // fires synchronously right after `doubleClick()` kicks it off (dragbox.js),
  // i.e. before that relayout's own event lands. So a flag set on
  // `onDoubleClick` and consumed by the very next `handleRelayout` call
  // reliably marks "the axes in THIS relayout were just reset, not zoomed" —
  // regardless of whether that reset shows up as `{axis}.autorange: true}`
  // (a plain-autorange axis) or `{axis}.range: [r0, r1]}` (an `equalAspect`
  // map's explicit range, `axes.js` `_rangeInitial0/1` — see AXIS_RANGE_KEY
  // above) — both would otherwise be captured as a fresh zoom.
  const pendingDoubleClickReset = useRef(false);
  const doubleClickResetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleDoubleClick = () => {
    pendingDoubleClickReset.current = true;
    clearTimeout(doubleClickResetTimeout.current);
    // See DOUBLE_CLICK_RESET_WINDOW_MS above: clears the flag on its own if
    // no relayout ever consumes it, so a stuck flag can't outlive this one
    // double-click and swallow a later, unrelated zoom.
    doubleClickResetTimeout.current = setTimeout(() => {
      pendingDoubleClickReset.current = false;
    }, DOUBLE_CLICK_RESET_WINDOW_MS);
  };
  useEffect(() => () => clearTimeout(doubleClickResetTimeout.current), []);
  const handleRelayout = (eventData: Record<string, unknown>) => {
    if (pendingDoubleClickReset.current) {
      pendingDoubleClickReset.current = false;
      clearTimeout(doubleClickResetTimeout.current);
      setLiveAxisRanges((prev) => {
        let next = prev;
        for (const key in eventData) {
          const axis = key.match(AXIS_RANGE_KEY)?.[1] ?? key.match(AXIS_AUTORANGE_KEY)?.[1];
          if (axis && next[axis] !== undefined) {
            if (next === prev) next = { ...prev };
            delete next[axis];
          }
        }
        return next;
      });
      return;
    }
    setLiveAxisRanges((prev) => {
      let next = prev;
      for (const key in eventData) {
        const autorangeMatch = key.match(AXIS_AUTORANGE_KEY);
        if (autorangeMatch && eventData[key]) {
          // The modebar's "Reset axes" click on a plain-autorange axis (an
          // equalAspect map's own reset is handled by `resetAxesButton`
          // below instead, and double-click by the branch above).
          if (next[autorangeMatch[1]] !== undefined) {
            if (next === prev) next = { ...prev };
            delete next[autorangeMatch[1]];
          }
          continue;
        }
        const rangeMatch = key.match(AXIS_RANGE_KEY);
        if (!rangeMatch) continue;
        const axis = rangeMatch[1];
        const idx = rangeMatch[2];
        const value = eventData[key];
        if (idx === undefined) {
          // A single relayout can carry the full `axis.range` array (e.g. a
          // programmatic reset) rather than the two indexed keys a manual
          // drag-zoom reports.
          if (Array.isArray(value) && value.length === 2 && value[0] != null && value[1] != null) {
            if (next === prev) next = { ...prev };
            next[axis] = [value[0] as number, value[1] as number];
          }
        } else if (typeof value === 'number') {
          const current = next[axis] ?? [NaN, NaN];
          const updated = [...current] as [number, number];
          updated[Number(idx)] = value;
          if (next === prev) next = { ...prev };
          next[axis] = updated;
        }
      }
      return next;
    });
  };

  // Memoized for the same reason as `figure` below: react-plotly.js also re-runs
  // `Plotly.react()` when `config` is a new reference.
  const plotConfig = useMemo((): Partial<Config> => {
    // Custom "Reset axes" modebar button, replacing plotly.js's own `resetScale2d`
    // (SPEC/2235 follow-up 2). Verified against a headless plotly.js+jsdom harness:
    // once `liveAxisRanges` has forced an axis's range at least once (which it has
    // by the time a user could click reset, since `handleRelayout` already fired
    // after their zoom), plotly.js's own `_preGUI` baseline for that axis has been
    // consumed (`applyUIRevisions`, plot_api.js) — so simply clearing the override
    // here and letting the next render pass the freshly authored range through is
    // enough; `uirevision` no longer has a stale baseline to fight it with. This
    // sidesteps guessing the shape of a reset event entirely (see the
    // AXIS_AUTORANGE_KEY comment above for why that guess fails on an
    // `equalAspect` map, which is exactly why this button exists).
    const resetAxesButton = {
      name: 'resetAxes',
      title: 'Reset axes',
      icon: RESET_AXES_ICON,
      click: () => setLiveAxisRanges({}),
    };

    // "Download data" (US 2488, SPEC/2488): the rows this chart draws, as CSV, built
    // and saved in the browser. Here, not per chart type, so every type gets it.
    const downloadDataButton = {
      name: 'downloadData',
      title: 'Download data as CSV',
      icon: DOWNLOAD_DATA_ICON,
      click: () => downloadCsv(chartDataToCsv(config, getDataset), exportFilename(config.title)),
    };

    // Modebar on hover only so it doesn't overlap the in-figure title; it is
    // excluded from PNG exports anyway. Hide the Plotly logo. Export PNG at
    // 3× for crisp figures in reports, named after the chart title.
    return {
      // Plotly's own resize listener is off: this component already measures
      // its container with a ResizeObserver and re-renders with a layout built
      // for the new size (that measurement is what every in-figure size is
      // derived from). Leaving `responsive: true` on top of it meant two
      // independent resize paths racing over the same figure.
      responsive: false,
      displayModeBar: 'hover',
      displaylogo: false,
      // A deliberate four: zoom in, back to the full extent, save the figure,
      // and save its data (US 2488). The default bar is 192px of buttons, most
      // of which (lasso, select, pan, autoscale, spike lines) have no meaning
      // for a report figure and were
      // only ever in the way of the title. The reset slot is `resetAxesButton`,
      // not plotly.js's own `resetScale2d` — see its definition above.
      modeBarButtons: [['zoom2d', resetAxesButton, 'toImage', downloadDataButton]],
      toImageButtonOptions: { format: 'png', scale: 3, filename: exportFilename(config.title) },
    };
  }, [config, getDataset]);

  // Every branch renders inside the ref'd wrapper: the ResizeObserver effect runs
  // once with [] deps, so if the first paint were an early return without the ref
  // (unsupported type, or data that resolves after mount) the element would never
  // be observed and figureSize would stay {0,0} for the chart's whole life —
  // silently disabling every fit for exactly the async-loaded charts.
  const measured = (children: ReactNode) => (
    <div ref={sizeRef} className="h-full w-full">
      {children}
    </div>
  );

  // Everything Plotly is handed is derived here, once per input that can change it
  // (bug "Algoritmo: Gráfico de dispersión"). react-plotly.js runs `Plotly.react()`
  // whenever `data` or `layout` arrives as a new reference, and fresh trace arrays
  // are a full recalc + redraw — so rebuilding them on EVERY render redrew a scatter
  // of one SVG point per block under each keystroke of an unrelated host form.
  // `fontEpoch` is listed though never read: the text fits measure against the
  // fonts loaded right now, so a font arriving must rebuild the layout.
  const figure = useMemo(() => {
    // Nothing is built for the frame before the first measurement: it is never drawn
    // (see the `pending` gate below), so the fits and canvas text measurements would
    // be thrown away.
    if (typeof ResizeObserver !== 'undefined' && !measuredOnce) return { kind: 'pending' as const };
    const def = getChart(type);
    if (!def) return { kind: 'unsupported' as const };

    const showGrid = options?.showGrid ?? true;
    const model = normalizeConfig(config, getDataset);

    // No rows resolved (missing inline data, unknown datasetId, or everything
    // filtered out): show an explicit empty state instead of a blank Plotly canvas.
    if (model.data.length === 0) return { kind: 'empty' as const };

    // The legend default comes from the type's capability, not a concrete-type
    // check here — the renderer stays agnostic to which chart kinds exist. A legend
    // with a single entry is just noise, so auto-hide it when the chart will render
    // only one entry — unless the consumer asked for the legend explicitly
    // (options.showLegend === true wins over the auto-hide).
    const wantLegend = options?.showLegend ?? def.capabilities.legendByDefault;
    const showLegend = wantLegend && (options?.showLegend === true || countLegendEntries(model) > 1);
    const autoFitText = options?.autoFitText ?? true;
    const ctx: RenderContext = {
      palette: DEFAULT_PALETTE,
      showGrid,
      showLegend,
      // The measurement, unconditionally. `autoFitText` opts out of WRAPPING, not of
      // proportional sizing: a widget that keeps its own line breaks still has to
      // render at the same visual weight in its card and in the figure it exports
      // to. makeLayout applies the flag to the wrap alone; the fit below is gated
      // separately.
      figureWidth: figureSize.w,
      figureHeight: figureSize.h,
      // Same predicate the annotation is drawn on below, so the layout cannot reserve
      // a gutter for a box that never renders.
      // Includes the rows the kit resolved for the shown level, not just the authored
      // ones — otherwise the layout reserves no gutter for a box that does render.
      hasStatistics: (model.statistics ?? config.statistics)?.length ? true : false,
      plotLeft,
      plotWidth,
      plotArea,
      // The zoom the layout is forced to below, so a type can size data-unit marks
      // against the range actually shown (spatialCategorical's `fitMarkerToCell`).
      liveAxisRanges,
    };
    const scale = figureScale(ctx.figureWidth);

    const traces = def.buildTraces(model, ctx);

    const layout: Record<string, unknown> = {
      // 'x unified' is the right default for line/bar/distribution. Set before the
      // merge so a chart type can override it via buildLayout (e.g. scatter prefers
      // 'closest' for xy clouds / 1:1 plots).
      hovermode: 'x unified',
      ...makeLayout(model, ctx),
      ...(def.buildLayout?.(model, ctx) ?? {}),
    };
    if (def.capabilities.supportsLimits && model.limits) {
      layout.shapes = resolveLimitsShapes(model.limits, scale);
    }
    // Stats box (opt-in) drawn inside the plot so it travels in PNG exports.
    const statistics = model.statistics ?? config.statistics;
    if (options?.showStatsBox && statistics?.length) {
      // The margins here are the AUTHORED base, not what Plotly will end up using —
      // and not even necessarily what makeLayout emitted, since a chart type's
      // `buildLayout` replaces the margin object wholesale (the merge above is by
      // top-level key). Each inset below corrects one side against the real value.
      const m = layout.margin as { l: number; r: number; t: number; b: number };
      // `margin.t` is only the AUTHORED base. The title is drawn with `automargin`,
      // so Plotly grows the real top margin to fit the title block — and that block
      // grows with every line the title wrapped into. Budgeting the box against the
      // base alone therefore overstated the height available by the whole title:
      // measured on a 3-line title, 62px assumed against ~134px real, a 30% too-tall
      // box in a plot area that had shrunk by the same amount.
      const topInset = Math.max(m.t, resolveTitleBlock(model, ctx).height);
      // …and the same correction at the BOTTOM. The default legend is anchored to
      // the container's bottom, and `autoexpand` grows the bottom margin to fit it,
      // so the plot area is shorter than the authored `margin.b` claims. Without
      // this the column budgets for space the legend already occupies and prints
      // straight through it — measured on the scatter, whose two-row legend the
      // TRANSICIONES block ran across. The gutter variants that pin the legend to
      // the top-right (`legendOutside`, and `columns`) expand nothing at the bottom.
      // `legendTakesGutter`, not the raw `legendOutside`: the gutter placement is DERIVED
      // from the deposit's shape now (see charts/shared/layout), so reading the option
      // meant budgeting for a legend at the bottom that makeLayout had already moved to
      // the right. The sibling `rightInset` below already goes through the same predicate;
      // this was the last raw reader left in the kit.
      const legendAtBottom =
        showLegend &&
        !legendTakesGutter(model, ctx) &&
        !(options?.statsBoxRight && options?.statsBoxColumns);
      const bottomInset =
        m.b +
        (legendAtBottom
          ? estimateLegendHeight(
              traces.filter((t) => t.showlegend !== false && typeof t.name === 'string').map((t) => t.name as string),
              ctx.figureWidth ?? 0,
              scaleFont(CHART_FONT, scale),
              // The plan view's heading rides above the entries and costs a line the
              // estimate would otherwise miss, leaving the box budgeted one line short.
              model.labels.legendHeading
            )
          : 0);
      // …and the same correction on the RIGHT. A `legendOutside` legend reserves its
      // gutter through Plotly's reserved-margin path, so `margin.r` says nothing about
      // it — on the plan view it is the 20px its own buildLayout authored, against a
      // gutter of ~320. Budgeting the box against that overstated the room by ~24%, and
      // since the value column is anchored to the RIGHT it grew leftwards and printed
      // on top of the labels ("Banco3.002,5 m"). US 2097.
      const rightInset = Math.max(m.r, resolveLegendGutter(model, ctx));
      // Preferred: derive the between-margins width from the MEASURED plot area and the
      // domain span it occupies, which is exact. The subtraction below is the first-paint
      // fallback, and it only ever overstates the room — the authored `margin.l` misses
      // what the tick labels added, so on the plan view it claimed ~24% more than exists
      // and the right-anchored value column grew back over the labels ("Banco3.002,5 m").
      const domain = (layout.xaxis as { domain?: number[] } | undefined)?.domain;
      const domainSpan = domain ? domain[1] - domain[0] : 1;
      const measuredPaperWidth =
        (ctx.plotWidth ?? 0) > 0 && domainSpan > 0 ? ctx.plotWidth! / domainSpan : 0;
      const paper =
        autoFitText && (ctx.figureWidth ?? 0) > 0 && (ctx.figureHeight ?? 0) > 0
          ? {
              width: measuredPaperWidth || ctx.figureWidth! - m.l - rightInset,
              height: ctx.figureHeight! - topInset - bottomInset,
            }
          : undefined;
      layout.annotations = statsAnnotations(
        statistics,
        options?.statsBoxRight,
        options?.statsBoxColumns,
        paper,
        scale
      );
    }
    // An explicit tooltip-off toggle always wins, regardless of the type default.
    if (options?.showTooltip === false) {
      layout.hovermode = false;
    }
    // Modebar horizontal, hover-only, excluded from PNG exports. Plotly pins it to
    // the graph div's top-right corner and `layout.modebar` exposes no position, so
    // it can collide with the centered in-figure title. That collision is now simply
    // ACCEPTED rather than paid for: the bar is translucent, appears only while the
    // pointer is in the figure, and never reaches a PNG — whereas the padding that
    // used to clear it cost 25px of plot height in every figure, always (see
    // TITLE_PAD_TOP). Keeping it horizontal is deliberate — stacked vertically it is
    // 178px tall and lands on the secondary Y axis title instead (measured: 16.8px),
    // trading one overlap for a worse one.
    layout.modebar = { orientation: 'h', bgcolor: 'rgba(255,255,255,0.6)' };
    // See the dataRevision hook above: identical across a level/elevation flip,
    // bumped on a genuine new run.
    layout.uirevision = `${config.id}:${dataRevision}`;
    // Force any axis the user has actively zoomed/panned to keep exactly that
    // range, overriding whatever the authored frame says — see liveAxisRanges
    // above for why `uirevision` alone doesn't reliably cover this for an
    // equalAspect map.
    for (const axis in liveAxisRanges) {
      layout[axis] = { ...(layout[axis] as object | undefined), range: liveAxisRanges[axis], autorange: false };
    }
    return { kind: 'plot' as const, traces, layout };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fontEpoch: see above
  }, [
    config,
    getDataset,
    figureSize,
    plotLeft,
    plotWidth,
    plotArea,
    dataRevision,
    liveAxisRanges,
    fontEpoch,
    measuredOnce,
  ]);

  // The first draw waits for the first measurement (SPEC/2539). Drawing the
  // unmeasured figure and then the measured one hands Plotly a single `react` that
  // both re-wraps the title block (automargin) and moves the legend (`legendY` is 0
  // until the height is known), and Plotly 3.5.1's partial path for that pair
  // (doLegend + layoutStyles) leaves the trace layer translated by the OLD top
  // margin: every curve drawn ~18px off its axis until a full redraw. It showed
  // after a conciliatron re-run, which remounts the charts once Plotly is already
  // loaded (on first load the lazy chunk arrives after the measurement, so it never
  // did). The gate must hold the <Plot> out of the first commit: react-plotly's
  // mount would otherwise draw before this component's layout effect measures. That
  // effect re-renders before paint, so this empty frame is never shown; without a
  // ResizeObserver nothing is ever measured, so nothing waits.
  if (figure.kind === 'pending') return measured(null);
  if (figure.kind === 'unsupported') {
    return measured(
      <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-50">
        <p className="text-gray-500">Tipo de gráfico no soportado: {type}</p>
      </div>
    );
  }
  if (figure.kind === 'empty') {
    return measured(
      <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-50">
        <p className="text-gray-500">Sin datos para mostrar</p>
      </div>
    );
  }
  const { traces, layout } = figure;

  return measured(
    <Suspense
      fallback={
        <div
          className="flex h-full w-full items-center justify-center"
          role="status"
          aria-label="Cargando gráfico"
        >
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      }
    >
      <Plot
        data={traces as Data[]}
        layout={layout as Partial<Layout>}
        useResizeHandler
        // Capture the live graph div so the export hook can snapshot it to PNG.
        onInitialized={(_, gd) => {
          register(config.id, gd);
          measurePlotLeft(gd);
          measurePlotArea(gd);
        }}
        onUpdate={(_, gd) => {
          register(config.id, gd);
          measurePlotLeft(gd);
          measurePlotArea(gd);
        }}
        onRelayout={handleRelayout}
        onDoubleClick={handleDoubleClick}
        style={{ width: '100%', height: '100%' }}
        config={plotConfig}
      />
    </Suspense>
  );
}
